import { type JSX } from "react";
import { Text, type LinkField } from "@sitecore-content-sdk/nextjs";
import { SectionHeroProps } from "components/section-hero/section-hero.props";
import Button from "../../atoms/Button";
import RichTextWithTable from "../../molecules/RichTextWithTable/RichTextWithTables";
import { extractHiddenBlock } from "../../utils/styles";
import { NoDataFallback } from "../../utils/NoDataFallback";

const DARK_BG_CLASSES = new Set(["bg-dark-blue", "bg-ecolab-blue"]);

interface SectionHeroCtaProps {
  field?: LinkField;
  defaultButtonType: string;
  isPageEditing: boolean;
  "data-testid"?: string;
}

const SectionHeroCta = ({
  field,
  defaultButtonType,
  isPageEditing,
  "data-testid": dataTestid,
}: SectionHeroCtaProps): JSX.Element | null => {
  const hasContent = !!(field?.value?.href || field?.value?.text);
  if (!isPageEditing && !hasContent) return null;

  const linkClass = field?.value?.class;
  const buttonType = linkClass || defaultButtonType;

  return (
    <Button
      field={field}
      buttonType={buttonType}
      className="!mb-0"
      iconName={linkClass ? "CHEVRON_BLUE_RIGHT" : ""}
      data-testid={dataTestid}
    />
  );
};

export const Default = (props: SectionHeroProps): JSX.Element => {
  console.log("---->[SectionHero] Component", props.fields);

  const { page, fields, params } = props;

  if (!fields) {
    return <NoDataFallback componentName="Section Hero" />;
  }

  const isPageEditing = page?.mode?.isEditing;

  const stylesArray = params?.Styles?.split(" ") ?? [];
  const isDarkBg =
    stylesArray.some((cls) => DARK_BG_CLASSES.has(cls)) ||
    stylesArray.includes("text-white");
  const textColor = isDarkBg ? "text-white" : "text-gray";
  const hiddenBlockStyles = extractHiddenBlock(params?.GridParameters);

  return (
    <div className={`${params?.Styles ?? ""} ${hiddenBlockStyles}`}>
      <div className="px-4 py-[25px] max-w-[var(--sitewide-container-max-width)] w-full mx-auto">
        {(isPageEditing || fields.Title?.value) && (
          <Text
            field={fields.Title}
            tag="h2"
            className={`${textColor} text-[28px] font-medium leading-[1.5] tracking-[0.4px] md:tracking-[0.8px] md:text-[32px] mt-0 mb-[15px]`}
            data-testid="section-hero-title"
          />
        )}
        {(isPageEditing || fields.SubTitle?.value) && (
          <Text
            field={fields.SubTitle}
            tag="h3"
            className={`${textColor} text-[20px] font-medium leading-[1.5] tracking-[0.4px] xl:tracking-[0.8px] md:text-[24px] mt-0 `}
            data-testid="section-hero-subtitle"
          />
        )}
        {(isPageEditing || fields.Description?.value) && (
          <RichTextWithTable
            field={fields.Description}
            className={`${textColor} richtext-content mb-[20px] text-base leading-[1.5] tracking-[0.4px] md:tracking-[0.8px] m-0 font-normal`}
            data-testid="section-hero-description"
          />
        )}
        <div className="flex flex-col lg:flex-row flex-wrap gap-y-4 items-start mt-5 [&:empty]:hidden">
          <SectionHeroCta
            field={fields.PrimaryCTA}
            defaultButtonType="primary"
            isPageEditing={isPageEditing}
            data-testid="section-hero-primaryCTA"
          />
          <SectionHeroCta
            field={fields.SecondaryCTA}
            defaultButtonType="secondary"
            isPageEditing={isPageEditing}
            data-testid="section-hero-secondaryCTA"
          />
        </div>
      </div>
    </div>
  );
};
